import { memo, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import LinearProgress from '@mui/material/LinearProgress';
import PropTypes from 'prop-types';
// Local files
import { FileInput } from './ImageUploader.styled';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useBlob from 'hooks/useBlob';
import useChecksum from 'hooks/useChecksum';

const ImageUploader = ({
  id,
  accept,
  onDelete,
  medias = null,
  setMedias = null,
  addFiles = false,
  setAddFiles = null,
  multiple = false,
}) => {
  const inputRef = useRef(null);
  const { processFiles } = useChecksum();
  const { processBlob, fileName, loading, percent } = useBlob();

  useEffect(() => {
    if (addFiles) {
      handleUploadClick();
      setMedias(null);
      setAddFiles(false);
    }
  }, [addFiles, setAddFiles, setMedias]);

  const handleUploadClick = () => inputRef.current.click();
  const handleFilesChange = ({ target: { files } }) => processFiles(files, ({ file, checksum, localUrl }) => processBlob({ file, checksum, localUrl }, blob => setMedias({...blob, localUrl})));
  
  return (
    <>
      <FileInput
        id={id}
        ref={inputRef}
        accept={accept}
        type='file'
        multiple={multiple}
        onChange={handleFilesChange}
        onClick={(event) => event.target.value = ''}
      />
      {loading && 
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ maxWidth: '33%', mr: 1 }}>
            <Typography variant="body2" color="text.secondary">{fileName}</Typography>
          </Box>
          <Box sx={{ width: '33%', mr: 1 }}>
            <LinearProgress variant="determinate" value={percent} />
          </Box>
          <Box sx={{ maxWidth: '33%' }}>
            <Typography variant="body2" color="text.secondary">{percent}</Typography>
          </Box>
        </Box>
      }
      {!!medias &&
        <Box mt={1} mb={1} sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <IconButton
            disabled={loading}
            onClick={() => onDelete(id)}
            color='error'
          >
            <DeleteIcon />
          </IconButton>
          <img
            style={{ flexShrink: 0, height: 'auto', width: 64 || '100%', objectFit: 'cover', borderRadius: '12px' }}
            src={medias.localUrl}
            alt='preview'
            data-private
          />
        </Box>
      }
    </>
  )
}

ImageUploader.propTypes = {
  id: PropTypes.string.isRequired,
  accept: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  formattedValue: PropTypes.object,
  addFiles: PropTypes.bool,
  setAddFiles: PropTypes.func,
  multiple: PropTypes.bool
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ImageUploader {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);